import React from 'react';
import { Rnd } from 'react-rnd';
import { CertificateTemplate, CertificateTextField } from '../../../apis/api-client';
import { Box, Typography } from '../../ui';

/**
 * Left pane of the certificate editor — the interactive canvas. Renders
 * the background image plus a draggable Rnd box per text field. All state
 * lives in the parent; this reports selection + drag results up.
 */
export default function CertificatePreviewCanvas({
  template,
  scale,
  backgroundUrl,
  selectedField,
  previewRef,
  liveValueForField,
  onSelectField,
  onDragStop,
}: {
  template: CertificateTemplate;
  scale: number;
  backgroundUrl: string | null;
  selectedField: string;
  previewRef: (node: HTMLDivElement | null) => void;
  liveValueForField: (field: CertificateTextField) => string;
  onSelectField: (fieldType: string) => void;
  onDragStop: (fieldType: string, x: number, y: number) => void;
}) {
  const previewHeight = template.height * scale;

  return (
    <Box sx={{ flex: 7, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
          LIVE PREVIEW
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Click a field to edit it, or drag to reposition.
        </Typography>
      </Box>
      <Box
        ref={previewRef}
        sx={{
          position: 'relative',
          width: '100%',
          height: previewHeight || 400,
          bgcolor: 'grey.200',
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'grey.300',
        }}
      >
        {/* Background image — the tenant's own upload. If the URL 404s (e.g.
            a stale SAS token) hide the broken image so the canvas falls back
            to its plain grey background rather than a broken-image icon. */}
        {backgroundUrl && (
          <Box
            component="img"
            src={backgroundUrl}
            alt="Certificate background"
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
              e.currentTarget.style.display = 'none';
            }}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        )}

        {/* Draggable text fields */}
        {template.textFields.map((field) => {
          const scaledX = field.x * scale;
          const scaledY = field.y * scale;
          const scaledFontSize = field.fontSize * scale;

          const previewText = liveValueForField(field);
          const textWidth = previewText.length * scaledFontSize * 0.6;
          let displayX = scaledX;
          if (field.textAlign === 'center') displayX = scaledX - textWidth / 2;
          else if (field.textAlign === 'right') displayX = scaledX - textWidth;

          const isSelected = selectedField === field.fieldType;
          return (
            <Rnd
              key={field.fieldType}
              position={{ x: displayX, y: scaledY }}
              onDragStop={(_e, d) => {
                let newX = d.x;
                if (field.textAlign === 'center') newX = d.x + textWidth / 2;
                else if (field.textAlign === 'right') newX = d.x + textWidth;
                onDragStop(field.fieldType, newX, d.y);
              }}
              onMouseDown={() => onSelectField(field.fieldType)}
              enableResizing={false}
              bounds="parent"
              style={{
                cursor: 'move',
                zIndex: isSelected ? 11 : 10,
              }}
            >
              <Box
                sx={{
                  // Selection chrome must NOT shift the text: `outline` paints
                  // outside the layout box and padding is 0, so the text's
                  // top-left sits exactly at (field.x, field.y). This is what
                  // keeps the coordinate meaning identical to the server render
                  // (see CertificateGenerationService.DrawTextField) with no
                  // scale-dependent offset. `outlineOffset` pushes the dashed
                  // box out a couple px purely for visual breathing room.
                  outline: '2px dashed',
                  outlineColor: isSelected ? 'primary.main' : 'rgba(0,0,0,0.3)',
                  outlineOffset: 2,
                  backgroundColor: isSelected ? 'rgba(var(--brand-rgb), 0.18)' : 'rgba(255,255,255,0.55)',
                  whiteSpace: 'nowrap',
                  userSelect: 'none',
                  transition: 'outline-color 120ms ease, background-color 120ms ease',
                }}
              >
                <Typography
                  sx={{
                    fontSize: scaledFontSize,
                    fontFamily: field.fontFamily,
                    fontWeight: field.fontWeight === 'bold' ? 700 : 400,
                    color: field.fontColor,
                    // lineHeight 1 so the line box equals the font size and its
                    // top aligns with the glyph ascent, matching ImageSharp's
                    // top-anchored draw on the server.
                    lineHeight: 1,
                  }}
                >
                  {previewText}
                </Typography>
              </Box>
            </Rnd>
          );
        })}
      </Box>
    </Box>
  );
}
